import { generatePath } from "react-router";
import { Basket } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { NudgeBanner } from "@acto/taskpane/field-sales-2/components/patterns/NudgeBanner";
import { useMeetingsAwaitingRecap } from "@acto/taskpane/core/data-access/meetings/useMeetingsAwaitingRecap";
import { useContributeChatScope } from "@acto/taskpane/core/chat-envelope";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";

interface RecapEntryBannerProps {
  className?: string;
  /**
   * When set, the banner is scoped to a single customer: the count covers only that
   * customer's open visits and the tap target is the customer's Besuchsberichte screen,
   * where the recap creation flow auto-starts (the banner is a shortcut straight into
   * writing the reports, not into the list).
   * Omitted on Home, where it counts all open visits and routes to the global recaps list.
   */
  clientId?: string;
  /** Which ambient-context scope the open-visits count belongs to. */
  contributeTo?: "home" | "customer-detail";
}

export function RecapEntryBanner({ className, clientId, contributeTo = "home" }: RecapEntryBannerProps) {
  const t = useTranslations("fieldSalesTwo.recaps");
  const navigate = useDirectionalNavigate();
  const { data: openMeetings, isFetching } = useMeetingsAwaitingRecap({ clientId });

  // Ambient context: the count is scope data even at 0 (the banner itself hides then).
  const recapsFragment = { open_recaps_count: openMeetings?.length };
  useContributeChatScope(contributeTo, recapsFragment);

  const count = openMeetings?.length ?? 0;

  // Entry point into the recaps page; hidden when there's nothing to recap.
  if (isFetching || count === 0) {
    return null;
  }

  const handleNavigateToRecaps = () => {
    if (clientId) {
      // Straight into writing the customer's open reports — the Besuchsberichte screen reads
      // `autoStartRecap` and opens the creation flow before its list ever paints.
      navigate(generatePath(ActoRoutes.CUSTOMER_RECAPS, { id: clientId }), {
        direction: "push",
        state: { autoStartRecap: true },
      });
      return;
    }
    navigate(ActoRoutes.OPEN_RECAPS, { direction: "push" });
  };

  return (
    <NudgeBanner
      className={className}
      icon={<Basket />}
      label={t("entryBanner", { count })}
      onClick={handleNavigateToRecaps}
    />
  );
}
